"use client";
import { useEffect, useState } from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { motion, useScroll, useTransform } from "framer-motion";

const CompuBioHeader = dynamic(
  () => import("@/components/CompuBioHeader"),
  { ssr: false }
);

const Reveal = dynamic(
  () => import("@/components/Reveal"),
  { ssr: false }
);

import HomeFooter from "@/components/Homefooter";
import DnaTV from "@/components/DnaTV";

/* ======================
   Dynamic Signature Hero
====================== */
const CompuBioHero = dynamic(
  () => import("@/components/AnimatedLogoHeader"),
  { ssr: false }
);

/* ======================
   Page
====================== */
export default function HomePage() {
  const [mounted, setMounted] = useState(false);

  const [activeFeature, setActiveFeature] = useState<number | null>(null);
  const [activeUseCase, setActiveUseCase] = useState<number | null>(null);
  const [activeFoundation, setActiveFoundation] = useState<number | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  const { scrollY } = useScroll();
  const ySlow = useTransform(scrollY, [0, 800], [0, 120]);

  const FEATURE_ITEMS = [
    {
      title: "Genomic Signal",
      desc: "Raw sequencing data, variants, and nucleotide-level patterns processed with validated pipelines.",
      long: `We ingest FASTQ/BAM/VCF and standard omics outputs, then run validated workflows for:
• QC + trimming + alignment
• Variant calling (SNP/INDEL) + filtering
• Annotation-ready outputs and cohort-ready tables

Everything is versioned and reproducible so you can trust the signal before interpretation.`,
    },
    {
      title: "AI Interpretation",
      desc: "Explainable AI models interpret genomic signals, quantify uncertainty, and rank biological relevance.",
      long: `CompuBio adds interpretable ML on top of your results:
• Feature importance & biological rationale
• Confidence / uncertainty scoring
• Biomarker ranking and cohort stratification

You can see why the model suggests a signal — not just the final label.`,
    },
    {
      title: "Actionable Insight",
      desc: "Clear reports and dashboards designed for researchers, clinicians, and decision-makers.",
      long: `We translate outputs into decision-ready views:
• Interactive dashboards & exportable reports
• Cohort summaries, QC flags, and key variants
• Clinician-friendly interpretation layers

The goal: reduce time-to-answer and improve confidence in conclusions.`,
    },
  ];
const USE_CASES = [
  {
    title: "Clinical Genomics",
    short:
      "Variant interpretation and evidence-backed genomic insights for clinical decision-making.",
    long: `CompuBio supports clinical genomics workflows with an emphasis on traceability and interpretability:

• Germline and somatic variant calling (SNPs / INDELs)
• Annotation with clinical databases (gene impact, pathogenicity)
• Cohort-level summaries and QC flags
• Explainable prioritization for clinical review

Designed to support translational research and clinical-grade reporting workflows.`,
  },
  {
    title: "Microbiome & Metagenomics",
    short:
      "Taxonomic profiling and functional insights from complex microbial communities.",
    long: `For microbiome and metagenomics studies, CompuBio provides:

• Amplicon and shotgun metagenomic pipelines
• Taxonomic abundance profiling
• Reproducible, containerized execution
• Cross-sample and longitudinal comparisons

Results are delivered in formats that biologists can explore and validate.`,
  },
  {
    title: "Precision Medicine",
    short:
      "Data-driven stratification and biomarker discovery for personalized insights.",
    long: `CompuBio enables precision medicine by integrating multi-dimensional data:

• Genotype–phenotype associations
• Machine-learning–based cohort stratification
• Explainable feature attribution
• Confidence-aware biomarker ranking

This allows researchers and clinicians to move from raw data to personalized, actionable conclusions.`,
  },
];
const FOUNDATIONS = [
  {
    title: "Execution Control",
    short:
      "Every analysis runs as a controlled experiment — not an ad-hoc script.",
    long: `CompuBio treats each analysis as a formal execution unit:

• Immutable inputs and parameter locking
• Deterministic pipeline resolution
• Explicit runtime environment capture
• Automatic failure detection and recovery

This ensures that results can be rerun, audited, and trusted — even months later.`,
  },
  {
    title: "Biological Context Encoding",
    short:
      "Raw signals are evaluated within biological and experimental context.",
    long: `Signals in CompuBio are never interpreted in isolation:

• Sample metadata and cohort structure
• Population and batch effects
• Functional annotations and pathways
• Experimental design constraints

This prevents misleading correlations and improves biological relevance.`,
  },
  {
    title: "Decision Readiness",
    short:
      "Outputs are structured for downstream scientific or clinical decisions.",
    long: `CompuBio outputs are decision-oriented by design:

• Confidence-aware summaries
• Clear prioritization instead of raw lists
• Research-grade and clinician-facing views
• Exportable evidence trails

The goal is not just analysis — but usable conclusions.`,
  },
];

  return (
    <main className="bg-[#070b1a] text-white overflow-hidden">

      {/* ======================================================
         1) SIGNATURE HERO (3D DNA + AI)
      ====================================================== */}
      {mounted && <CompuBioHeader />}
      {mounted && <CompuBioHero />}

      
      {/* ======================================================
         2) SCIENTIFIC VALUE / WHY
      ====================================================== */}
     <section className="relative py-40 overflow-hidden">

  {/* ================= BACKGROUND (CompuBio signature) ================= */}
  {mounted && (
  <motion.div
    style={{ y: ySlow }}
    className="absolute inset-0"
  >

    {/* base gradient */}
    <div className="absolute inset-0 bg-gradient-to-b from-[#070b1a] via-[#0b1024] to-[#050814]" />


    {/* radial bio glow */}
    <div className="absolute inset-0
                    bg-[radial-gradient(circle_at_50%_30%,rgba(34,211,238,0.12),transparent_55%)]" />

    {/* subtle computation grid */}
    <div
      className="absolute inset-0 opacity-[0.08]"
      style={{
        backgroundImage:
          "linear-gradient(rgba(255,255,255,0.08) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.08) 1px, transparent 1px)",
        backgroundSize: "48px 48px",
      }}
    />
  </motion.div>
)}


  {/* ================= CONTENT ================= */}
  <div className="relative z-10 max-w-7xl mx-auto px-8">

    {/* ===== HEADER ===== */}
    <Reveal>
      <div className="text-center mb-28">
        <h2 className="text-3xl md:text-5xl font-extrabold mb-8">
          Bioinformatics, without fragmentation
        </h2>

        <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
          Traditional bioinformatics workflows are fragmented across scripts,
          tools, and environments. CompuBio unifies sequencing data,
          computational pipelines, and AI interpretation into a single,
          reproducible system.
        </p>
      </div>
    </Reveal>

    {/* ================= PIPELINE STORY ================= */}
    <div className="grid md:grid-cols-3 gap-14">
      {FEATURE_ITEMS.map((item, i) => {
        const active = activeFeature === i;

        return (
          <Reveal key={i} delay={i * 0.15}>
            <button
              type="button"
              onClick={() => setActiveFeature(i)}
              onMouseEnter={() => setActiveFeature(i)}
              aria-pressed={active}
              className={`
                relative text-left p-10 rounded-3xl
                backdrop-blur-xl border
                transition-all duration-300
                ${
                  active
                    ? "bg-white/10 border-cyan-400/60 shadow-[0_0_70px_rgba(34,211,238,0.15)]"
                    : "bg-white/5 border-white/10 hover:border-cyan-400/40"
                }
              `}
            >
              {/* glow */}
              {active && (
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-3xl
                             bg-cyan-400/10 blur-2xl pointer-events-none"
                />
              )}

              <div className="relative z-10 space-y-4">
                <h3 className="text-xl font-semibold text-cyan-300">
                  {item.title}
                </h3>

                <p className="text-slate-300 text-sm leading-relaxed">
                  {item.desc}
                </p>

                <span
                  className={`inline-flex items-center gap-2 text-xs font-semibold
                    ${active ? "text-cyan-200" : "text-indigo-200"}`}
                >
                  {active ? "Viewing details" : "Hover / tap for details"}
                  <span className="opacity-70">→</span>
                </span>
              </div>
            </button>
          </Reveal>
        );
      })}
    </div>

    {/* ================= DETAIL PANEL ================= */}
    {activeFeature !== null && (
      <Reveal>
        <div
          className="
            mt-24 rounded-3xl
            border border-cyan-400/25
            bg-gradient-to-br from-black/70 via-[#050b1f] to-black/80
            p-12
            shadow-[0_0_90px_rgba(34,211,238,0.12)]
          "
        >
          <div className="flex items-center justify-between gap-6">
            <h3 className="text-2xl font-extrabold text-cyan-200">
              {FEATURE_ITEMS[activeFeature].title}
            </h3>

            <button
              onClick={() => setActiveFeature(null)}
              className="text-xs font-semibold px-4 py-2 rounded-full
                         border border-white/10 bg-white/5
                         hover:bg-white/10 text-indigo-200 transition"
            >
              Close
            </button>
          </div>

          <p className="mt-8 text-slate-200 leading-8 whitespace-pre-line max-w-4xl">
            {FEATURE_ITEMS[activeFeature].long}
          </p>
        </div>
      </Reveal>
    )}
  </div>
</section>


      {/* ======================================================
         4) VISUAL PANEL (DNA + Platform)
      ====================================================== */}
      <section id="platform" className="relative py-36">
        <div className="max-w-7xl mx-auto px-8 grid md:grid-cols-2 gap-20 items-center">

          <Reveal>
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                A unified bioinformatics platform
              </h2>

              <p className="text-slate-300 mb-8 leading-relaxed max-w-xl">
                CompuBio is designed for genomics, metagenomics, and clinical
                research teams that require traceability, reproducibility, and
                explainable AI.
              </p>

              <ul className="space-y-4 text-slate-300 text-sm">
                <li>• End-to-end genomic and microbiome pipelines</li>
                <li>• Variant calling, annotation, and prioritization</li>
                <li>• AI-powered disease and phenotype prediction</li>
                <li>• Versioned, auditable, and reproducible workflows</li>
              </ul>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <div className="bg-white/5 backdrop-blur-xl border border-white/10
                            rounded-3xl p-10">
              <DnaTV />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ======================================================
         5) USE CASES (CLINICAL / RESEARCH)
      ====================================================== */}
      <section className="relative py-32 bg-[#070b1a]">
  <div className="max-w-7xl mx-auto px-8 text-center">
    <Reveal>
      <h2 className="text-3xl md:text-5xl font-extrabold mb-16">
        Built for research and clinical impact
      </h2>
    </Reveal>

    {/* ===== USE CASE CARDS ===== */}
    <div className="grid md:grid-cols-3 gap-12">
      {USE_CASES.map((item, i) => {
        const active = activeUseCase === i;

        return (
          <Reveal key={i} delay={i * 0.15}>
            <button
              type="button"
              onClick={() => setActiveUseCase(i)}
              onMouseEnter={() => setActiveUseCase(i)}
              aria-pressed={active}
              className={`
                relative text-left
                bg-white/5 backdrop-blur-xl
                border rounded-3xl
                p-10 space-y-4
                transition-all duration-300
                ${
                  active
                    ? "border-indigo-400/60 bg-white/10 shadow-[0_0_60px_rgba(99,102,241,0.15)]"
                    : "border-white/10 hover:border-indigo-400/40"
                }
              `}
            >
              {/* soft glow */}
              {active && (
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-3xl bg-indigo-400/10 blur-2xl pointer-events-none"
                />
              )}

              <div className="relative z-10">
                <h3 className="text-xl font-semibold text-indigo-300">
                  {item.title}
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed">
                  {item.short}
                </p>

                <div className="pt-4">
                  <span
                    className={`
                      inline-flex items-center gap-2 text-xs font-semibold
                      ${active ? "text-indigo-200" : "text-slate-400"}
                    `}
                  >
                    {active ? "Viewing details" : "Hover / tap for details"}
                    <span className="opacity-70">→</span>
                  </span>
                </div>
              </div>
            </button>
          </Reveal>
        );
      })}
    </div>

    {/* ===== DETAIL PANEL ===== */}
    {activeUseCase !== null && (
      <div
        className="
          mt-20
          rounded-3xl
          border border-indigo-400/25
          bg-gradient-to-br from-black/70 via-[#0b1024] to-black/80
          p-10
          shadow-[0_0_80px_rgba(99,102,241,0.12)]
          max-w-5xl mx-auto
        "
      >
        <div className="flex items-center justify-between gap-6">
          <h3 className="text-2xl font-extrabold text-indigo-200">
            {USE_CASES[activeUseCase].title}
          </h3>

          <button
            type="button"
            onClick={() => setActiveUseCase(null)}
            className="
              text-xs font-semibold
              px-3 py-2 rounded-full
              border border-white/10
              bg-white/5 hover:bg-white/10
              text-indigo-200
              transition
            "
          >
            Close
          </button>
        </div>

        <p className="mt-6 text-slate-200 leading-8 whitespace-pre-line">
          {USE_CASES[activeUseCase].long}
        </p>
      </div>
    )}
  </div>
</section>

{/* ======================================================
   PIPELINES (REPRODUCIBLE · TRACEABLE · CLINICAL-GRADE)
====================================================== */}
<section className="relative py-32 overflow-hidden">
  {/* ===== BACKGROUND MOTION ===== */}
  {mounted && (
  <motion.div
    style={{ y: ySlow }}
    className="absolute inset-0 bg-gradient-to-b from-[#070b1a] via-[#0b1024] to-[#050814]"
  />
)}


  <div className="relative z-10 max-w-6xl mx-auto px-8">

    {/* ===== HEADER ===== */}
    <Reveal>
      <div className="text-center mb-20">
        <h2 className="text-3xl md:text-5xl font-extrabold mb-8">
          What happens behind every CompuBio analysis
        </h2>

        <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
  CompuBio is engineered as an operational system, not a collection of tools.
  Every analysis follows explicit execution rules, biological context,
  and decision-oriented outputs — ensuring results remain valid beyond
  a single experiment or environment.
</p>
      </div>
    </Reveal>

    {/* ===== FOUNDATION CARDS ===== */}
    <div className="grid md:grid-cols-3 gap-12">
      {FOUNDATIONS.map((f, i) => {
        const active = activeFoundation === i;

        return (
          <Reveal key={i} delay={i * 0.15}>
            <button
              type="button"
              onClick={() => setActiveFoundation(i)}
              onMouseEnter={() => setActiveFoundation(i)}
              aria-pressed={active}
              className={`
                relative text-left p-8 rounded-3xl
                backdrop-blur-xl border transition-all duration-300
                ${
                  active
                    ? "bg-white/10 border-cyan-400/50 shadow-[0_0_50px_rgba(34,211,238,0.12)]"
                    : "bg-white/5 border-white/10 hover:border-cyan-400/30"
                }
              `}
            >
              {active && (
                <div
                  aria-hidden
                  className="absolute inset-0 rounded-3xl
                             bg-cyan-400/10 blur-2xl pointer-events-none"
                />
              )}

              <div className="relative z-10 space-y-4">
                <h3 className="text-xl font-semibold text-cyan-300">
                  {f.title}
                </h3>

                <p className="text-sm text-slate-300 leading-relaxed">
                  {f.short}
                </p>

                <span className="inline-flex items-center gap-2
                                 text-xs font-semibold text-indigo-200">
                  {active ? "Viewing details" : "Hover / tap for details"}
                  <span className="opacity-70">→</span>
                </span>
              </div>
            </button>
          </Reveal>
        );
      })}
    </div>

    {/* ===== DETAIL PANEL ===== */}
    {activeFoundation !== null && (
      <Reveal>
        <div
          className="
            mt-20 rounded-3xl
            border border-cyan-400/25
            bg-gradient-to-br from-black/70 via-[#050b1f] to-black/80
            p-10 shadow-[0_0_80px_rgba(34,211,238,0.10)]
          "
        >
          <div className="flex items-center justify-between gap-6">
            <h3 className="text-2xl font-extrabold text-cyan-200">
              {FOUNDATIONS[activeFoundation].title}
            </h3>

            <button
              onClick={() => setActiveFoundation(null)}
              className="text-xs font-semibold px-3 py-2 rounded-full
                         border border-white/10 bg-white/5
                         hover:bg-white/10 text-indigo-200 transition"
            >
              Close
            </button>
          </div>

          <p className="mt-6 text-slate-200 leading-8 whitespace-pre-line max-w-4xl">
            {FOUNDATIONS[activeFoundation].long}
          </p>
        </div>
      </Reveal>
    )}
  </div>
</section>

{/* ======================================================
   SCIENCE (BIOLOGY × COMPUTATION × AI)
====================================================== */}
<section
  id="science"
  className="relative py-40 bg-[#070b1a]"
>
  <div className="max-w-6xl mx-auto px-8">

    <Reveal>
      <div className="text-center mb-24">
        <h2 className="text-3xl md:text-5xl font-extrabold mb-6">
          Built on biological reality, not black boxes
        </h2>
        <p className="text-lg md:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
          CompuBio integrates biological domain knowledge with modern
          computational science to ensure every result is explainable,
          interpretable, and scientifically grounded.
        </p>
      </div>
    </Reveal>

    <div className="space-y-20">

      {/* Block 1 */}
      <Reveal>
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <h3 className="text-2xl font-semibold text-emerald-300 mb-4">
              Biology-first modeling
            </h3>
            <p className="text-slate-300 leading-relaxed">
              Our analytical models respect biological constraints.
              Variant effects, microbial abundance, and phenotype associations
              are evaluated within biologically meaningful contexts —
              not treated as abstract signals.
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 text-sm text-slate-300">
            • Functional annotation-aware models<br />
            • Pathway- and gene-level aggregation<br />
            • Population-aware statistical correction
          </div>
        </div>
      </Reveal>

      {/* Block 2 */}
      <Reveal delay={0.15}>
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 text-sm text-slate-300">
            • Explainable ML (feature attribution)<br />
            • Uncertainty quantification<br />
            • Confidence-aware ranking
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-cyan-300 mb-4">
              Explainable AI, by design
            </h3>
            <p className="text-slate-300 leading-relaxed">
              AI models inside CompuBio are not opaque predictors.
              Each inference is accompanied by confidence estimates,
              feature contributions, and biological interpretation layers.
            </p>
          </div>
        </div>
      </Reveal>

      {/* Block 3 */}
      <Reveal delay={0.3}>
        <div className="grid md:grid-cols-2 gap-14 items-center">
          <div>
            <h3 className="text-2xl font-semibold text-indigo-300 mb-4">
              Clinical & research readiness
            </h3>
            <p className="text-slate-300 leading-relaxed">
              From exploratory research to translational and clinical contexts,
              CompuBio supports controlled execution, version locking,
              and traceable scientific decision-making.
            </p>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-3xl p-8 text-sm text-slate-300">
            • Audit-friendly execution logs<br />
            • Reproducible experiment snapshots<br />
            • Regulatory-aligned reporting
          </div>
        </div>
      </Reveal>

    </div>
  </div>
</section>

      {/* ======================================================
         6) CALL TO ACTION (CALM)
      ====================================================== */}
      <section className="relative py-32 bg-[#050814] text-center">
        <Reveal>
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Ready to explore CompuBio?
          </h2>
          <p className="text-slate-300 mb-10">
            Access a unified bioinformatics platform designed for scientific
            rigor and real-world impact.
          </p>

          <div className="flex justify-center gap-6">
            <Link
  href="/login"
  className="bg-white text-black px-8 py-4 rounded-xl font-semibold hover:scale-105 transition"
>

              Get Started
            </Link>

            <Link
              href="/dashboard"
              className="border border-white/20 px-8 py-4 rounded-xl
                         hover:bg-white/10 transition"
            >
              Open Dashboard
            </Link>
          </div>
        </Reveal>
      </section>

      {/* ======================================================
         FOOTER
      ====================================================== */}
      <HomeFooter />
    </main>
  );
}
